import React, { useState } from 'react';
import { Modal } from '../common/Modal';
import { useTheme } from '../../context/shared/ThemeContext'
import PatientForm, { PatientFormData } from './PatientForm';
import apiService from '../../services/shared/apiService';
import { useNotification } from '../../hooks/useNotification';
import { useAuth } from '../../context/shared/AuthContext'

interface AddPatientModalProps {
  isOpen: boolean;
  onClose: () => void;
  onPatientAdded: () => void;
}

const initialFormData: PatientFormData = {
  displayName: '',
  email: '',
  dob: '',
  sex: '',
  pronouns: '',
  phoneNumber: '',
  street1: '',
  city: '',
  state: '',
  zip: '',
  country: '',
  emergencyContactName: '',
  emergencyContactPhone: '',
  primaryConcerns: '',
  pastDiagnoses: '',
  medicationList: '',
  treatmentPlanSummary: '',
  consentMetrics: true,
  consentMessaging: true,
};

const AddPatientModal: React.FC<AddPatientModalProps> = ({ isOpen, onClose, onPatientAdded }) => {
  const { themeClasses } = useTheme();
  const { user } = useAuth();
  const { addToast } = useNotification();
  const [formData, setFormData] = useState<PatientFormData>(initialFormData);
  const [isSaving, setIsSaving] = useState(false);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value, type } = e.target;
    const checked = (e.target as HTMLInputElement).checked;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));
  };

  const handleAdd = async () => {
    if (!user) {
      addToast('Therapist not authenticated.', 'error');
      return;
    }
    setIsSaving(true);
    try {
      await apiService.makeRequest('/users/add-patient', { 
        method: 'POST', 
        data: { therapistId: user?.uid, patientData: formData }
      });
      addToast('Patient added successfully!', 'success');
      onPatientAdded();
      onClose();
      setFormData(initialFormData);
    } catch (error: any) {
      addToast(error.message || 'Failed to add patient.', 'error');
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose}>
      <div className="p-4 sm:p-6 max-h-[90vh] overflow-y-auto">
        <h2 className="text-xl font-bold mb-6 text-center">Add New Patient</h2>
        
        <PatientForm formData={formData} onFormChange={handleChange} />

        <div className="mt-8 flex justify-end space-x-3">
          <button type="button" className={`px-4 py-2 rounded-md ${themeClasses.buttonSecondary}`} onClick={onClose}>
            Cancel
          </button>
          <button 
            type="button" 
            className="px-4 py-2 rounded-md text-white bg-indigo-600 hover:bg-indigo-700 disabled:opacity-50" 
            onClick={handleAdd}
            disabled={isSaving}
          >
            {isSaving ? 'Adding...' : 'Add Patient'}
          </button>
        </div>
      </div>
    </Modal>
  );
};

export default AddPatientModal; 